import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Mail, Lock, Loader } from 'lucide-react';
import { SUPABASE_URL } from '../config/supabase';
import logoNagy from '../assets/logos/logonagy.png';

function LoginPage() {
  const { signIn, signUp } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedSubscription, setAcceptedSubscription] = useState(false);

  const handleAuth = async () => {
    if (!email || !password) {
      setAuthError(t('login.errors.missingCredentials'));
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setAuthError(t('login.errors.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setAuthError(t('login.errors.passwordTooShort'));
      return;
    }

    if (!isLogin && (!acceptedTerms || !acceptedPrivacy || !acceptedSubscription)) {
      setAuthError(t('login.errors.acceptAllTerms'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');
    setAuthSuccess('');

    try {
      let result;
      if (isLogin) {
        result = await signIn(email, password);
      } else {
        result = await signUp(email, password);
      }

      if (result.error) {
        setAuthError(result.error.message || t('login.errors.authenticationFailed'));
      } else if (result.user) {
        setAuthSuccess(isLogin ? t('login.success.signedIn') : t('login.success.accountCreated'));
      } else if (!isLogin) {
        setAuthSuccess(t('login.success.accountCreatedVerify'));
      }
    } catch (error) {
      setAuthError(t('login.errors.networkError'));
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-300 from-10% via-sky-300 via-50% to-emerald-300 to-100% flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logoNagy} alt="VideoSync Logo" className="h-40 mx-auto mb-4" />
          <p className="text-gray-600">{t('login.title')}</p>
        </div>

        {authError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{authError}</p>
          </div>
        )}

        {authSuccess && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">{authSuccess}</p>
          </div>
        )}

        <form className="space-y-4" onSubmit={e => { e.preventDefault(); handleAuth(); }}>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Mail className="inline mr-2" size={16} />
              {t('login.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Lock className="inline mr-2" size={16} />
              {t('login.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
            />
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Lock className="inline mr-2" size={16} />
                {t('login.confirmPassword')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none"
              />
            </div>
          )}

          {!isLogin && (
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 mr-2 h-4 w-4 text-sky-500 border-gray-300 rounded focus:ring-sky-500"
                />
                <label htmlFor="acceptTerms" className="text-sm text-gray-700">
                  <Trans
                    i18nKey="login.acceptTerms"
                    components={{
                      termsLink: <Link to="/terms" className="text-sky-600 hover:text-sky-700 underline" target="_blank" rel="noopener noreferrer" />
                    }}
                  />
                </label>
              </div>
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="acceptPrivacy"
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  className="mt-1 mr-2 h-4 w-4 text-sky-500 border-gray-300 rounded focus:ring-sky-500"
                />
                <label htmlFor="acceptPrivacy" className="text-sm text-gray-700">
                  <Trans
                    i18nKey="login.acceptPrivacy"
                    components={{
                      privacyLink: <Link to="/privacy" className="text-sky-600 hover:text-sky-700 underline" target="_blank" rel="noopener noreferrer" />
                    }}
                  />
                </label>
              </div>
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="acceptSubscription"
                  checked={acceptedSubscription}
                  onChange={(e) => setAcceptedSubscription(e.target.checked)}
                  className="mt-1 mr-2 h-4 w-4 text-sky-500 border-gray-300 rounded focus:ring-sky-500"
                />
                <label htmlFor="acceptSubscription" className="text-sm text-gray-700">
                  {t('login.acceptSubscription')}
                </label>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={authLoading}
            className="w-full py-3 bg-gradient-to-r from-sky-500 from-10% to-emerald-500 to-90% text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-sky-700 transition-all disabled:opacity-50"
          >
            {authLoading ? (
              <span className="flex items-center justify-center">
                <Loader className="animate-spin mr-2" size={20} />
                {isLogin ? `${t('common.buttons.login')}...` : `${t('common.buttons.register')}...`}
              </span>
            ) : (
              isLogin ? t('common.buttons.login') : t('common.buttons.register')
            )}
          </button>
 
          {/*<div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Vagy folytasd ezzel</span>
            </div>
          </div>*/} 

          {/*<div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => signInWithProvider('google')}
              className="flex items-center justify-center px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-sm font-medium text-gray-700">Google</span>
            </button>
            
            <button
              onClick={() => signInWithProvider('apple')}
              className="flex items-center justify-center px-4 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="text-sm font-medium text-gray-700">Apple</span>
            </button>
          </div>*/}
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setAuthError('');
              setAuthSuccess('');
            }}
            className="text-sky-600 hover:text-sky-700 font-medium text-sm"
          >
            {isLogin ? t('login.toggleToRegister') : t('login.toggleToLogin')}
          </button>
        </div>

        {SUPABASE_URL.includes('your-project') && (
          <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-800">
              <strong>⚠️ Setup Required:</strong> {t('login.setupRequired')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginPage;